import React, { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useCrossFlow, Journeys } from '@atlassiansox/cross-flow-support';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Pressable, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY } from '@atlassian/jira-common-constants/src/project-template-type-keys.tsx';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectCreateDrawer } from '@atlassian/jira-project-create-drawer-context/src/index.tsx';
import { servicedeskStandardToPremiumModalEntryPoint } from '@atlassian/jira-servicedesk-standard-to-premium-modal/entrypoint.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import type { card_projectsDirectoryTemplatesSidebar_TemplateCard$key } from '@atlassian/jira-relay/src/__generated__/card_projectsDirectoryTemplatesSidebar_TemplateCard.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { JSM_PRODUCT_KEY } from '@atlassian/jira-servicedesk-standard-to-premium-modal/src/services/premium-trial-period/constants.tsx';
import {
	PROJECT_CREATE_SOURCE,
	PROJECT_TYPE_TARGETS_MAP,
	getProductTypeCcpReferenceIdMapForEnv,
} from '../../constants.tsx';
import { messages } from '../../messages.tsx';
import { checkShouldPromptJsmTrial } from '../../utils.tsx';
import CardIcon from './icon/index.tsx';
import { buildCrossflowExtensions } from './utils.tsx';

interface Props {
	dataRef: card_projectsDirectoryTemplatesSidebar_TemplateCard$key;
	setTemplatePreviewKey: Dispatch<SetStateAction<string | null>>;
	recommendedProductIds: string[];
	index: number;
	refreshEnabled?: boolean;
}
export const TemplateCard = ({
	dataRef,
	recommendedProductIds,
	setTemplatePreviewKey,
	index,
	refreshEnabled,
}: Props) => {
	const environment = useEnvironment();
	const { fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const crossFlow = useCrossFlow();

	const {
		methods: { open: openProjectCreate },
	} = useProjectCreateDrawer();

	const cardEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'card',
	});

	const cardHoveredEvent = createAnalyticsEvent({
		action: 'hovered',
		actionSubject: 'card',
	});

	const template = useFragment<card_projectsDirectoryTemplatesSidebar_TemplateCard$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment card_projectsDirectoryTemplatesSidebar_TemplateCard on JiraProjectListViewTemplateItem {
				title @required(action: THROW)
				shortDescription @required(action: THROW)
				description @required(action: THROW)
				key @required(action: THROW)
				isProductLicensed @required(action: THROW)
				isPremiumOnly @required(action: THROW)
				canCreate @required(action: THROW)
				productKey @required(action: THROW)
				recommendationSessionId @required(action: THROW)
				templateType @required(action: THROW)
				...icon_projectsDirectoryTemplatesSidebar_CardIcon
			}
		`,
		dataRef,
	);

	const { formatMessage } = useIntl();

	const {
		title,
		shortDescription,
		description,
		key,
		isProductLicensed,
		isPremiumOnly,
		productKey,
		templateType,
		recommendationSessionId,
		canCreate,
	} = template;

	const { shouldPromptJsmTrial } = checkShouldPromptJsmTrial({
		templateKey: key,
		isProductLicensed,
		canCreate,
	});
	const [isJsmPremiumUpgradeModalOpen, setIsJsmPremiumUpgradeModalOpen] = useState(false);
	const cloudId = useCloudId();
	const entryPoint = useEntryPoint(servicedeskStandardToPremiumModalEntryPoint, {
		cloudId,
		productKey: JSM_PRODUCT_KEY,
	});
	const triggerRef = useEntryPointButtonTrigger(entryPoint.entryPointActions);

	const extensions = useMemo(() => {
		const mergedExtensions = buildCrossflowExtensions({
			templateId: key,
			recommendationSessionId,
			templateType,
		});
		if (
			key === IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY &&
			fg('fix_advanced_itsm_recommendation_cross_flow')
		) {
			Object.assign(mergedExtensions, {
				edition: 'premium',
			});
		}
		return mergedExtensions;
	}, [key, recommendationSessionId, templateType]);

	const onClick = useCallback(() => {
		fireUIAnalytics(cardEvent, 'templateCard', {
			key,
			productKey,
			isProductLicensed,
			isPremiumOnly,
			templateType,
			recommendationSessionId,
			extensions,
		});

		if (shouldPromptJsmTrial) {
			setIsJsmPremiumUpgradeModalOpen(true);
			return;
		}

		if (
			!isProductLicensed &&
			crossFlow.isEnabled &&
			PROJECT_TYPE_TARGETS_MAP[productKey] &&
			getProductTypeCcpReferenceIdMapForEnv(environment)[productKey]
		) {
			fireInteractedUIEvent(
				{
					action: 'clicked',
					actionSubject: 'card',
					actionSubjectId: 'templateCard',
				},
				{
					recommendedProductIds,
					recommendationSession: {
						id: recommendationSessionId,
						type: 'appRec',
					},
					targetProductId: getProductTypeCcpReferenceIdMapForEnv(environment)[productKey],
					source: 'projectsDirectorySidebarSection',
				},
			);
			// eslint-disable-next-line @atlassian/growth/no-deprecated-apis
			crossFlow.api.open({
				journey: Journeys.GET_STARTED,
				targetProduct: PROJECT_TYPE_TARGETS_MAP[productKey],
				sourceComponent: 'projectsDirectorySidebar',
				sourceContext: 'templateCard',
				extensions,
			});
			return;
		}

		openProjectCreate({
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			selectedProjectType: productKey.toLowerCase() as ProjectType,
			showExperienceSelection: false,
			defaultSelectedTemplateKey: key,
			source: PROJECT_CREATE_SOURCE,
			recommendationSessionId,
		});
	}, [
		cardEvent,
		key,
		productKey,
		isProductLicensed,
		isPremiumOnly,
		templateType,
		recommendationSessionId,
		extensions,
		crossFlow,
		environment,
		openProjectCreate,
		fireInteractedUIEvent,
		shouldPromptJsmTrial,
		recommendedProductIds,
	]);

	const onPreviewDismiss = () => {
		setTemplatePreviewKey((current) => {
			if (current === key) {
				return null;
			}
			return current;
		});
	};

	const onPreviewStart = () => {
		fireUIAnalytics(cardHoveredEvent, 'templateCard', {
			key,
			productKey,
			isProductLicensed,
			templateType,
			isPremiumOnly,
			sidebarVisualRefreshEnabled: refreshEnabled,
		});
		setTemplatePreviewKey(key);
	};

	const templateLozenge = () => {
		if (!isProductLicensed)
			return <Lozenge appearance="new">{formatMessage(messages.tryLozenge)}</Lozenge>;
		if (isPremiumOnly)
			return <Lozenge appearance="new">{formatMessage(messages.premiumLozenge)}</Lozenge>;
	};

	return (
		<>
			<Box as="li" xcss={refreshEnabled ? PaddedCardStylesVariation : PaddedCardStyles}>
				<Pressable
					padding={refreshEnabled ? undefined : 'space.200'}
					data-testId={`projects-directory-templates-sidebar.ui.template-card-${index}`}
					xcss={refreshEnabled ? CardStylesVariation : CardStyles}
					aria-describedby={
						fg('hix-7639_fix_aria-description')
							? `projects-directory-templates-sidebar.ui.template-card-${index}-desc`
							: undefined
					}
					onFocus={onPreviewStart}
					onBlur={onPreviewDismiss}
					onClick={onClick}
					onMouseEnter={onPreviewStart}
					onMouseLeave={onPreviewDismiss}
					tabIndex={0}
					{...(shouldPromptJsmTrial && {
						ref: triggerRef,
					})}
				>
					{fg('hix-7639_fix_aria-description') && (
						<VisuallyHidden
							id={`projects-directory-templates-sidebar.ui.template-card-${index}-desc`}
						>
							{description}
						</VisuallyHidden>
					)}
					<Inline space="space.150" alignBlock="center">
						<CardIcon keyRef={template} refreshEnabled={refreshEnabled} />
						{refreshEnabled ? (
							<Stack as="div" space="space.025" xcss={TemplateTextContentStyles}>
								<Box as="span" xcss={TitleStylesVariation}>
									<Box as="span" xcss={TitleTextStyles}>
										{title}
									</Box>
									<Box as="span">{templateLozenge()}</Box>
								</Box>
								<Box as="p" xcss={DescriptionStyles}>
									{shortDescription}
								</Box>
							</Stack>
						) : (
							<Stack as="div" space="space.050">
								<Box as="span" xcss={TitleStyles}>
									{/* Intentional trailing space for lozenge */}
									{`${title} `}
									{templateLozenge()}
								</Box>
								<Box as="p" xcss={DescriptionStyles}>
									{shortDescription}
								</Box>
							</Stack>
						)}
					</Inline>
				</Pressable>
			</Box>
			{isJsmPremiumUpgradeModalOpen && (
				<ModalEntryPointContainer
					entryPointReferenceSubject={entryPoint.entryPointReferenceSubject}
					id="standard-to-premium-modal"
					packageName="jira-cmdb-upsell-page"
					teamName="ITOps Mithril" // assigning to Mithril for the experiment
					entryPointActions={entryPoint.entryPointActions}
					runtimeProps={{
						onClose: () => {
							setIsJsmPremiumUpgradeModalOpen(false);
						},
						origin: 'templateCard',
					}}
				/>
			)}
		</>
	);
};

const PaddedCardStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	paddingBlock: 'space.100',
});

const CardStyles = xcss({
	width: '100%',
	textAlign: 'left',
	borderRadius: 'radius.small',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	':hover': {
		background: 'elevation.surface.overlay',
		boxShadow: 'elevation.shadow.overlay',
	},
	':focus': {
		background: 'elevation.surface.overlay',
		boxShadow: 'elevation.shadow.overlay',
	},
});

const TitleStyles = xcss({
	font: token('font.body'),
	color: 'color.text',
});

const DescriptionStyles = xcss({
	margin: '0',
	color: 'color.text.subtlest',
	font: token('font.body.UNSAFE_small'),
});

// GRW-1532

const TitleStylesVariation = xcss({
	font: token('font.body'),
	color: 'color.text',
	display: 'flex',
	columnGap: 'space.100',
	flexDirection: 'row',
	alignItems: 'center',
});

const TitleTextStyles = xcss({
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
});

const TemplateTextContentStyles = xcss({
	minWidth: '0',
});

const CardStylesVariation = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.200',
	width: '100%',
	textAlign: 'left',
	backgroundColor: 'elevation.surface',
	position: 'relative',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':focus': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		zIndex: 'card',
	},
});

const PaddedCardStylesVariation = xcss({
	marginBlockStart: 'space.0',
});
